import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useBlocker } from 'react-router-dom';
import cl from 'classnames';
import { BlockerFunction } from '@remix-run/router/router';
import { ProblemHeadset } from 'src/models';
import {
  ALL_ITEM_ID,
  HEADSETS,
  HOTELS,
  REPORTS,
  PROBLEMS,
} from 'src/constants';
import {
  usePage,
  useContextData,
  useXlsxWriter,
  XlsxTableConfig,
  useLanguage,
} from 'src/hooks';
import {
  compareString,
  daysFromToday,
  formatDate,
  formatShortDate,
  formatTime,
  formatWorkingMode,
  getLastActivityColor,
  hybridCompare,
} from 'src/helper';
import { useAppDispatch } from 'src/store';
import {
  useProblemHeadsetCompanies,
  useProblemHeadsets,
  fetchProblemHeadsetsAction,
  useProblemHeadsetsState,
} from 'src/services/problemHeadsets';
import { resetProblemHeadsetsState } from 'src/store/reducers/problemHeadsets';
import {
  PageSwitcher,
  StickyTable,
  StickyTableConfig,
  useStickyCols,
  BatteryStatus,
  CardStatus,
  Switcher,
  CustomSorter,
  TableMarkers,
} from 'src/components';
import {
  Button,
  GroupFilter,
  FilterModal,
  FilterModalButton,
  useSearchInput,
  Tooltip,
  FilterModalIndicator,
  Link,
  Checkbox,
} from 'src/components/general';
import { ReactComponent as TooltipIcon } from 'src/icons/tooltip.svg';
import styles from './reportsProblemsStyle.module.scss';

const companySorter = (a: ProblemHeadset, b: ProblemHeadset) =>
  compareString(a.companyName, b.companyName);

const batteryFilter = ({ charging, battery, updatedAt }: ProblemHeadset) =>
  (!charging && battery !== null && battery <= 15) ||
  daysFromToday(updatedAt) > 3;

const cardFilter = ({ cardState }: ProblemHeadset) => cardState !== 'ok';

const testFilter = ({ isTest }: ProblemHeadset) => isTest;

const prepareMarkers = (list: ProblemHeadset[], singleGroup: boolean) => {
  const markers: TableMarkers = { l1: [], l2: [] };

  if (!list.length) {
    return markers;
  }

  let group: 'l1' | 'l2' = 'l1';
  let prevCompanyName = list[0].companyName;

  return list.reduce((res, { companyName, id }) => {
    if (!singleGroup && companyName !== prevCompanyName) {
      group = group === 'l1' ? 'l2' : 'l1';
    }
    prevCompanyName = companyName;

    return { ...res, [group]: [...res[group], id] };
  }, markers);
};

const problemTypes = ['all', 'battery', 'card', 'test'] as const;
type ProblemType = (typeof problemTypes)[number];

const additionalParams = ['showFirst', 'filterByProblem'] as const;
type AdditionalParam = (typeof additionalParams)[number];

export const ReportsProblems = () => {
  const {
    header: { width: headerWidth },
    navigator: { setBackUrl },
  } = useContextData();
  const dispatch = useAppDispatch();
  const state = useProblemHeadsetsState();
  const headsets = useProblemHeadsets();
  const companies = useProblemHeadsetCompanies();
  const { t, td } = useLanguage();
  const {
    access: { accessMap },
  } = usePage();
  const { writeToXlsx } = useXlsxWriter<ProblemHeadset>();

  const [problemType, setProblemType] = useState<ProblemType>('all');
  const onProblemTypeChange = useCallback((value: string) => {
    setProblemType(value as ProblemType);
  }, []);

  const [paramsSet, setParamsSet] = useState<Set<AdditionalParam>>(new Set());
  const onSelectParam = useCallback((id: string, value: boolean) => {
    setParamsSet(new Set(value ? [id as AdditionalParam] : []));
  }, []);

  const showFirst = useMemo(() => paramsSet.has('showFirst'), [paramsSet]);
  const filterByProblem = useMemo(
    () => paramsSet.has('filterByProblem'),
    [paramsSet],
  );

  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);

  const {
    searchString,
    searchInput,
    modalSearchInput,
    applySearch,
    cancelSearch,
    clearSearch,
    focusInput,
  } = useSearchInput({
    inputClassName: styles.searchInput,
    modalInputClassName: styles.modalSearchInput,
    disabled: state.isPending,
    onEnter: () => {
      setShowSearchModal(false);
    },
  });

  useEffect(() => {
    if (showSearchModal) {
      focusInput();
    }
  }, [showSearchModal, focusInput]);

  const [selectedCompany, setSelectedCompany] = useState<Set<string>>(
    new Set(),
  );
  const [modalSelectedCompany, setModalSelectedCompany] = useState<Set<string>>(
    new Set(),
  );

  const resetFilter = useCallback(() => {
    setSelectedCompany(new Set());
    setModalSelectedCompany(new Set());
    clearSearch();
  }, [clearSearch]);

  const filteredByCompanies = useMemo(
    () => Boolean(selectedCompany.size) && !selectedCompany.has(ALL_ITEM_ID),
    [selectedCompany],
  );

  const selectAllCompanies = useMemo(
    () => ({
      id: ALL_ITEM_ID,
      label: t('general.allCompanies'),
    }),
    [t],
  );

  const labelXlsxConfigs: XlsxTableConfig<ProblemHeadset>[] = useMemo(
    () => [
      {
        title: t('page.reports.problems.label'),
        field: 'label',
        key: 'label',
      },
      {
        title: t('page.reports.problems.headsetId'),
        field: 'id',
        key: 'id',
      },
      {
        title: t('page.reports.problems.firmware'),
        field: 'firmware',
        key: 'firmware',
        render: (value, headset) =>
          `${headset.firmware || '-'}-${headset.name || t('general.unknown')}`,
      },
      {
        title: t('page.reports.problems.isTest'),
        field: 'isTest',
        key: 'isTest',
        render: (value) => t(`general.${value ? 'yes' : 'no'}`),
      },
    ],
    [t],
  );

  const companyNameXlsxConfig: XlsxTableConfig<ProblemHeadset> = useMemo(
    () => ({
      title: t('page.reports.problems.companyName'),
      field: 'companyName',
      key: 'companyName',
    }),
    [t],
  );

  const cardStateXlsxConfig: XlsxTableConfig<ProblemHeadset> = useMemo(
    () => ({
      title: t('page.reports.problems.cardState'),
      key: 'cardState',
      field: 'cardState',
      render: (value: string) => t(`general.${value === 'ok' ? 'yes' : 'no'}`),
    }),
    [t],
  );

  const otherXlsxConfig: XlsxTableConfig<ProblemHeadset>[] = useMemo(
    () => [
      {
        title: t('page.reports.problems.roomLabel'),
        key: 'roomLabel',
        field: 'roomLabel',
        render: (value) => value || '-',
      },
      {
        title: t('page.reports.problems.mode'),
        key: 'mode',
        field: 'mode',
        render: (value) => td(formatWorkingMode(value)),
      },
      {
        title: t('page.reports.problems.battery'),
        key: 'battery',
        field: 'battery',
        render: (value) => `${value || 0}%`,
      },
      ...(problemType === 'card' ? [] : [cardStateXlsxConfig]),
      {
        title: t('page.reports.problems.cardState'),
        key: 'cardState',
        field: 'cardState',
        render: (value: string) =>
          t(`general.${value === 'ok' ? 'yes' : 'no'}`),
      },
      {
        title: t('page.reports.problems.updatedAt'),
        key: 'updatedAtDate',
        field: 'updatedAt',
        render: (value) => formatDate(value),
      },
      {
        title: t('page.reports.problems.time'),
        key: 'updatedAtTime',
        field: 'updatedAt',
        render: (value) => formatTime(value),
      },
    ],
    [t, td, problemType, cardStateXlsxConfig],
  );

  const xlsxConfig: XlsxTableConfig<ProblemHeadset>[] = useMemo(() => {
    switch (problemType) {
      case 'all':
        return [...labelXlsxConfigs, companyNameXlsxConfig, ...otherXlsxConfig];
      case 'battery':
        return [companyNameXlsxConfig, ...labelXlsxConfigs, ...otherXlsxConfig];
      case 'card':
        return [
          companyNameXlsxConfig,
          cardStateXlsxConfig,
          ...labelXlsxConfigs,
          ...otherXlsxConfig,
        ];
      case 'test':
        return [companyNameXlsxConfig, ...labelXlsxConfigs, ...otherXlsxConfig];
    }
  }, [
    cardStateXlsxConfig,
    companyNameXlsxConfig,
    problemType,
    labelXlsxConfigs,
    otherXlsxConfig,
  ]);

  const labelConfig: StickyTableConfig<ProblemHeadset> = useMemo(
    () => ({
      title: t('page.reports.problems.label'),
      key: 'label',
      field: 'label',
      sorter: (a, b, sortOrder) =>
        hybridCompare(a.label, b.label) * (sortOrder === 'descend' ? -1 : 1),
      render: (label, headset) => {
        return (
          <div
            className={cl(
              styles.label,
              !accessMap[HEADSETS].length && styles.inactive,
            )}
          >
            <div>
              <Link
                className={styles.title}
                src={
                  accessMap[HEADSETS].length
                    ? `/${HOTELS}/${headset.companyId}/${HEADSETS}/${headset.id}/${accessMap[HEADSETS][0]}`
                    : null
                }
              >
                {label || '-'}
              </Link>
              <div className={styles.secondary}>
                {headset.firmware || '-'}
                {'-'}
                {headset.name || t('general.unknown')}
                <Tooltip
                  text={[
                    t('tooltip.headsetId', { id: headset.phyDeviceId }),
                    t('tooltip.humanId', { id: headset.humanId }),
                  ]}
                  tooltipClassName={styles.tooltip}
                  arrowClassName={styles.tooltipArrow}
                  withClipboard
                >
                  <TooltipIcon />
                </Tooltip>
              </div>
            </div>
            {headset.isTest && (
              <div className={cl(styles.tag, styles.blue)}>
                {t('general.test')}
              </div>
            )}
          </div>
        );
      },
    }),
    [accessMap, t],
  );

  const companyNameConfig: StickyTableConfig<ProblemHeadset> = useMemo(
    () => ({
      title: t('page.reports.problems.companyName'),
      key: 'companyName',
      field: 'companyName',
      sorter: (a, b, sortOrder) =>
        compareString(a.companyName, b.companyName) *
        (sortOrder === 'descend' ? -1 : 1),
      render: (value, headset) => (
        <Link
          className={cl(
            styles.companyName,
            !accessMap[HOTELS].length && styles.inactive,
          )}
          src={
            accessMap[HOTELS].length
              ? `/${HOTELS}/${headset.companyId}/${accessMap[HOTELS][0]}`
              : null
          }
        >
          {value}
        </Link>
      ),
    }),
    [accessMap, t],
  );

  const cardStateConfig: StickyTableConfig<ProblemHeadset> = useMemo(
    () => ({
      title: t('page.reports.problems.cardState'),
      key: 'cardState',
      field: 'cardState',
      sorter: (a, b, sortOrder) =>
        (Number(a.cardState === 'ok') - Number(b.cardState === 'ok')) *
        (sortOrder === 'descend' ? -1 : 1),
      render: (label: string, headset) => (
        <CardStatus
          cardState={headset.cardState}
          onlyWarning
          style={{ marginTop: 8 }}
        />
      ),
    }),
    [t],
  );

  const otherConfig: StickyTableConfig<ProblemHeadset>[] = useMemo(
    () => [
      {
        title: t('page.reports.problems.roomLabel'),
        key: 'roomLabel',
        field: 'roomLabel',
        sorter: (a, b, sortOrder) =>
          compareString(a.roomLabel, b.roomLabel) *
          (sortOrder === 'descend' ? -1 : 1),
        render: (value) => (
          <div className={styles.roomLabel}>{value || '-'}</div>
        ),
      },
      {
        title: t('page.reports.problems.mode'),
        key: 'mode',
        field: 'mode',
        sorter: (a, b, sortOrder) =>
          compareString(a.mode, b.mode) * (sortOrder === 'descend' ? -1 : 1),
        render: (value) => <div>{td(formatWorkingMode(value))}</div>,
      },
      {
        title: t('page.reports.problems.battery'),
        key: 'battery',
        field: 'battery',
        sorter: (a, b, sortOrder) => {
          const left = a.battery || -1;
          const right = b.battery || -1;
          return (left - right) * (sortOrder === 'descend' ? -1 : 1);
        },
        render: (value, headset) => (
          <BatteryStatus
            battery={value}
            charging={headset.charging}
            className={styles.tag120}
          />
        ),
      },
      ...(problemType === 'card' ? [] : [cardStateConfig]),
      {
        title: t('page.reports.problems.updatedAt'),
        key: 'updatedAt',
        field: 'updatedAt',
        sorter: (a, b, sortOrder) => {
          const dateA = new Date(a.updatedAt).getTime();
          const dateB = new Date(b.updatedAt).getTime();
          return (dateA - dateB) * (sortOrder === 'descend' ? -1 : 1);
        },
        render: (value) => (
          <div
            className={cl(
              styles.tag,
              styles[getLastActivityColor(value)],
              styles.tag120,
            )}
          >
            {formatShortDate(value)}
          </div>
        ),
      },
    ],
    [t, problemType, cardStateConfig, td],
  );

  const config: StickyTableConfig<ProblemHeadset>[] = useMemo(() => {
    switch (problemType) {
      case 'all':
        return [labelConfig, companyNameConfig, ...otherConfig];
      case 'battery':
        return [companyNameConfig, labelConfig, ...otherConfig];
      case 'card':
        return [
          companyNameConfig,
          cardStateConfig,
          labelConfig,
          ...otherConfig,
        ];
      case 'test':
        return [companyNameConfig, labelConfig, ...otherConfig];
    }
  }, [
    cardStateConfig,
    companyNameConfig,
    problemType,
    labelConfig,
    otherConfig,
  ]);

  const filteredRecords = useMemo(() => {
    return headsets.filter(
      (headset) =>
        (searchString ? headset.label?.includes(searchString) : true) &&
        (selectedCompany.size ? selectedCompany.has(headset.companyId) : true),
    );
  }, [headsets, searchString, selectedCompany]);

  const postFilteredRecords = useMemo(() => {
    if (!filterByProblem) {
      return filteredRecords;
    }

    switch (problemType) {
      case 'all':
        return filteredRecords;
      case 'battery':
        return filteredRecords.filter(batteryFilter);
      case 'card':
        return filteredRecords.filter(cardFilter);
      case 'test':
        return filteredRecords.filter(testFilter);
    }
  }, [problemType, filteredRecords, filterByProblem]);

  const xlsxData = useMemo(() => {
    if (!showFirst && !filterByProblem) {
      return postFilteredRecords;
    }

    return [...postFilteredRecords].sort(companySorter);
  }, [postFilteredRecords, showFirst, filterByProblem]);

  const selectCompany = useCallback(
    (item: string, value: boolean, selected: Set<string>) => {
      if (item === ALL_ITEM_ID) {
        return selected.size - (selected.has(ALL_ITEM_ID) ? 1 : 0) <
          companies.length
          ? new Set([ALL_ITEM_ID, ...companies.map((item) => item.id)])
          : new Set<string>();
      }

      const updatedIDs = new Set(selected);
      updatedIDs.delete(ALL_ITEM_ID);

      if (value) {
        updatedIDs.add(item);
      } else {
        updatedIDs.delete(item);
      }

      if (updatedIDs.size === companies.length) {
        updatedIDs.add(ALL_ITEM_ID);
      }

      return updatedIDs;
    },
    [companies],
  );

  const {
    stickyCols,
    stickyColsFilter,
    modalStickyColsFilter,
    applySCChanges,
    resetSCChanges,
  } = useStickyCols({
    config,
    className: styles.stickyCols,
    modalClassName: styles.modalStickyCols,
  });

  const blocker = useCallback<BlockerFunction>(
    ({ nextLocation: { pathname } }) => {
      setBackUrl(
        pathname.startsWith(`/${HOTELS}/`) ? `/${REPORTS}/${PROBLEMS}` : null,
      );

      return false;
    },
    [setBackUrl],
  );

  useBlocker(blocker);

  useEffect(() => {
    dispatch(fetchProblemHeadsetsAction({}));
  }, [dispatch]);

  useEffect(
    () => () => {
      dispatch(resetProblemHeadsetsState());
    },
    [dispatch],
  );

  const customSorter: CustomSorter<ProblemHeadset> | undefined = useMemo(() => {
    if (!showFirst && !filterByProblem) {
      return undefined;
    }

    switch (problemType) {
      case 'all':
        return undefined;
      case 'battery':
        return (a, b, sortOption) => {
          const primary =
            Number(
              Boolean(!b.charging && b.battery !== null && b.battery <= 15),
            ) -
            Number(
              Boolean(!a.charging && a.battery !== null && a.battery <= 15),
            );

          if (primary !== 0) {
            return primary;
          }

          const sortOrder =
            sortOption && sortOption.key === 'companyName'
              ? sortOption.sortOrder
              : null;

          return (
            compareString(a.companyName, b.companyName) *
            (sortOrder === 'descend' ? -1 : 1)
          );
        };
      case 'card':
        return (a, b, sortOption) => {
          const primary =
            Number(Boolean(b.cardState !== 'ok')) -
            Number(Boolean(a.cardState !== 'ok'));

          if (primary !== 0) {
            return primary;
          }

          const sortOrder =
            sortOption && sortOption.key === 'companyName'
              ? sortOption.sortOrder
              : null;

          return (
            compareString(a.companyName, b.companyName) *
            (sortOrder === 'descend' ? -1 : 1)
          );
        };
      case 'test':
        return (a, b, sortOption) => {
          const primary = Number(b.isTest) - Number(a.isTest);

          if (primary !== 0) {
            return primary;
          }

          const sortOrder =
            sortOption && sortOption.key === 'companyName'
              ? sortOption.sortOrder
              : null;

          return (
            compareString(a.companyName, b.companyName) *
            (sortOrder === 'descend' ? -1 : 1)
          );
        };
    }
  }, [problemType, showFirst, filterByProblem]);

  const markedIdsMap: Record<ProblemType, TableMarkers> = useMemo(
    () => ({
      all: prepareMarkers(
        [...filteredRecords].sort(companySorter),
        !showFirst && !filterByProblem,
      ),
      battery: prepareMarkers(
        filteredRecords.filter(batteryFilter).sort(companySorter),
        !showFirst && !filterByProblem,
      ),
      card: prepareMarkers(
        filteredRecords.filter(cardFilter).sort(companySorter),
        !showFirst && !filterByProblem,
      ),
      test: prepareMarkers(
        filteredRecords.filter(testFilter).sort(companySorter),
        !showFirst && !filterByProblem,
      ),
    }),
    [filteredRecords, showFirst, filterByProblem],
  );

  const markers = useMemo(
    () => (problemType === 'all' ? undefined : markedIdsMap[problemType]),
    [problemType, markedIdsMap],
  );

  const switcherList = useMemo(
    () => [
      ...problemTypes.map((type) => ({
        id: type,
        label: t(`page.reports.sales.type.${type}`),
        count: markedIdsMap[type].l1.length + markedIdsMap[type].l2.length,
      })),
      {
        id: 'showFirst',
        label: t('page.reports.sales.type.showFirst'),
        isCb: true,
      },
      {
        id: 'filterByProblem',
        label: t('page.reports.sales.type.filterByProblem'),
        isCb: true,
      },
    ],
    [t, markedIdsMap],
  );

  return (
    <div className={styles.container}>
      <FilterModal
        show={showSearchModal}
        close={() => {
          setShowSearchModal(false);
        }}
        onApply={() => {
          applySearch();
          setShowSearchModal(false);
        }}
        onCancel={() => {
          cancelSearch();
          setShowSearchModal(false);
        }}
        isSearch
      >
        {modalSearchInput}
      </FilterModal>
      <FilterModal
        show={showFiltersModal}
        close={() => {
          setShowFiltersModal(false);
        }}
        onApply={() => {
          applySCChanges();
          setSelectedCompany(modalSelectedCompany);
          setShowFiltersModal(false);
        }}
        onCancel={() => {
          resetSCChanges();
          setModalSelectedCompany(new Set());
        }}
      >
        <GroupFilter
          label={t('general.selectedCompanies')}
          selectedIDs={modalSelectedCompany}
          items={[
            selectAllCompanies,
            ...companies.map((item) => ({
              id: item.id,
              label: item.company,
            })),
          ]}
          selectItem={(item, value) => {
            setModalSelectedCompany(
              selectCompany(item, value, modalSelectedCompany),
            );
          }}
          size="small"
          className={styles.modalGroupFilter}
        />
        {modalStickyColsFilter}
      </FilterModal>
      <div
        className={styles.topSection}
        style={{
          minWidth: headerWidth,
          maxWidth: headerWidth,
        }}
      >
        <div
          className={cl(
            styles.pageSwitcherContainer,
            accessMap[REPORTS].length === 1 && styles.inactive,
          )}
        >
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <Switcher
            currentId={problemType}
            list={switcherList}
            onChange={onProblemTypeChange}
            onlyMobile
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSwitcherSelect}
            selectedCbIDs={paramsSet}
            selectCbItem={onSelectParam}
          />
          <div className={styles.filterButtons}>
            <FilterModalButton
              setShow={setShowSearchModal}
              icon="search"
              badge={Boolean(searchString)}
              disabled={state.isPending}
            />
            <FilterModalButton
              setShow={setShowFiltersModal}
              badge={filteredByCompanies}
              disabled={state.isPending}
            />
            <FilterModalButton
              setShow={() => {
                writeToXlsx(
                  {
                    config: xlsxConfig,
                    data: xlsxData,
                  },
                  'reports_problems.xlsx',
                  true,
                );
              }}
              icon="exportToXlsx"
              disabled={state.isPending}
            />
            <FilterModalIndicator
              icon="search"
              text={searchString}
              clear={clearSearch}
              disabled={state.isPending}
            />
          </div>
        </div>
        <div className={styles.typeSwitcherContainer}>
          <Switcher
            currentId={problemType}
            list={switcherList}
            onChange={onProblemTypeChange}
            onlyDesktop
            className={styles.typeSwitcher}
          />
          <div className={styles.typeCheckboxGroup}>
            <Checkbox
              label={t('page.reports.sales.type.showFirst')}
              checked={showFirst}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                onSelectParam('showFirst', event.target.checked);
              }}
              className={styles.typeCheckbox}
              labelClassName={styles.typeCheckboxTitle}
            />
            <Checkbox
              label={t('page.reports.sales.type.filterByProblem')}
              checked={filterByProblem}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                onSelectParam('filterByProblem', event.target.checked);
              }}
              className={styles.typeCheckbox}
              labelClassName={styles.typeCheckboxTitle}
            />
          </div>
        </div>
        <div className={styles.filtersWrapper}>
          <div className={styles.filters}>
            <GroupFilter
              label={t('general.selectedCompanies')}
              selectedIDs={selectedCompany}
              items={[
                selectAllCompanies,
                ...companies.map((item) => ({
                  id: item.id,
                  label: item.company,
                })),
              ]}
              selectItem={(item, value) => {
                const updatedIDs = selectCompany(item, value, selectedCompany);

                setSelectedCompany(updatedIDs);
                setModalSelectedCompany(updatedIDs);
              }}
              className={styles.selectCompany}
              disabled={state.isPending}
            />
            {searchInput}
            {stickyColsFilter}
          </div>
          <div className={styles.buttons}>
            <Button
              label={t('component.exportToXlsx.button')}
              design="lightBlue"
              className={styles.button}
              icon="exportToXlsx"
              onClick={() => {
                writeToXlsx(
                  {
                    config: xlsxConfig,
                    data: xlsxData,
                  },
                  'reports_problems.xlsx',
                  true,
                );
              }}
              disabled={Boolean(state.isPending)}
            />
          </div>
        </div>
      </div>
      <StickyTable
        config={config}
        data={postFilteredRecords}
        withPaginator
        customSorter={customSorter}
        markers={markers}
        isLoading={state.isPending}
        stickyCols={stickyCols}
        className={styles.table}
        isFiltered={Boolean(searchString) || filteredByCompanies}
        resetFilter={resetFilter}
        notFoundText={t('general.resetFilters')}
        rowKey="id"
      />
    </div>
  );
};

export default ReportsProblems;
